/**
 * A saved search as a Discover section (#2291): its title and description,
 * "See all" (the search, opened in Discover's results), and a ⋯ menu to edit
 * the search, move the section, or take it off the page.
 *
 * It runs the saved query fresh for the viewer (`"me"` is whoever looks),
 * `count` results, and only once the section comes within 200 px of the
 * viewport, so a long page does not fire every search on load. It has its
 * own placeholder, error with "Try again", and a compact row when nothing
 * matches (never hidden: an empty saved search is still the viewer's). An
 * answer for an older query, count or attempt is dropped.
 *
 * Its filter box (#2302) narrows it on the server: the typed text, after a
 * pause, is ANDed with the saved query (`withFilterText`), at the same count;
 * an answer for older text is dropped, and the results it replaces stay in
 * view until it comes. Clearing the box shows the saved results again (kept,
 * not asked for again); the saved search itself never changes. The setting
 * (`filterBox`): Auto shows the box once the saved search has more than the
 * section holds (`hasMore`), and keeps it while it has text or focus; Always
 * on and Always off override.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { InvalidModelQueryError, queryModels, type ModelQuery } from '@/api/modelQuery';
import type { DiscoverSection, FilterBoxMode, SavedSearch } from '@/api/discoverPreferences';
import { SectionHead, SectionMessage, ItemsView, SectionSkeleton } from '../DiscoverCards';
import type { DiscoverItem } from '../useDiscoverData';
import { DISCOVER_PATH, queryToSearch } from '../search/searchUrl';
import { toModelQuery } from '../search/searchSyntax';
import { countOf, filterTextRoom, fromModelQuery, variantOf, withFilterText } from './discoverLayout';
import { FILTER_BOX_LABELS } from './discoverRegistry';
import SectionMenu, { type MenuAction } from './SectionMenu';
import { useNearViewport } from './useNearViewport';

/** How long the filter box waits after the last key before it asks. */
export const FILTER_DEBOUNCE_MS = 300;

interface Answer {
  key: string;
  attempt: number;
  status: 'ready' | 'error' | 'invalid';
  items: DiscoverItem[];
  hasMore: boolean;
}

/** An answer for the saved query (`base`, its key) narrowed by the filter box's `text`. */
interface Filtered extends Answer {
  base: string;
  text: string;
}

function answerOf(key: string, attempt: number, run: Promise<{ data: unknown[]; hasMore: boolean }>): Promise<Answer> {
  return run.then(
    (page) => ({ key, attempt, status: 'ready', items: page.data as unknown as DiscoverItem[], hasMore: page.hasMore }),
    (err: unknown) => ({ key, attempt, status: err instanceof InvalidModelQueryError ? 'invalid' : 'error', items: [], hasMore: false }),
  );
}

export interface SavedSearchSectionProps {
  section: DiscoverSection;
  search: SavedSearch;
  navigate: (path: string) => void;
  onEdit: (search: SavedSearch) => void;
  onMove: (dir: -1 | 1) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onRemove: () => void;
  /** Its filter box setting (#2302). */
  filterBox: FilterBoxMode;
  /** The settings the server allows, for the ⋯ menu: none from a server without them. */
  filterBoxModes: readonly FilterBoxMode[];
  onFilterBox: (mode: FilterBoxMode) => void;
  className?: string;
}

export default function SavedSearchSection({
  section, search, navigate, onEdit, onMove, canMoveUp, canMoveDown, onRemove,
  filterBox, filterBoxModes, onFilterBox, className = 'mb-9',
}: Readonly<SavedSearchSectionProps>) {
  const variant = variantOf(section);
  const count = countOf(section);
  const asked = useMemo(() => fromModelQuery(search.query), [search.query]);
  const json = JSON.stringify(toModelQuery(asked));
  const query = useMemo(() => JSON.parse(json) as ModelQuery, [json]);
  const key = `${json}|${count}`;
  const { ref, near } = useNearViewport<HTMLElement>('200px');
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<Answer | null>(null);

  // the saved search's own results: fetched once near, kept while the box filters
  useEffect(() => {
    if (!near) return;
    let alive = true;
    void answerOf(key, attempt, queryModels(query, { limit: count })).then((a) => { if (alive) setAnswer(a); });
    return () => { alive = false; };
  }, [near, key, query, count, attempt]);

  const current = answer?.key === key && answer.attempt === attempt ? answer : null;

  // The filter box: what is typed, and the text it asks for (after a pause).
  const [typed, setTyped] = useState('');
  const [filter, setFilter] = useState('');
  const [focused, setFocused] = useState(false);
  const [filterAttempt, setFilterAttempt] = useState(0);
  const [filtered, setFiltered] = useState<Filtered | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const showBox = filterBox === 'on' || (filterBox === 'auto' && (current?.hasMore === true || typed !== '' || focused));
  const active = showBox ? filter : '';
  const filterJson = active ? JSON.stringify(toModelQuery(withFilterText(asked, active))) : '';
  const filterQuery = useMemo(() => (filterJson ? JSON.parse(filterJson) as ModelQuery : null), [filterJson]);
  const filterKey = `${key}|${filterJson}`;

  useEffect(() => {
    if (!near || !filterQuery) return;
    let alive = true;
    void answerOf(filterKey, filterAttempt, queryModels(filterQuery, { limit: count })).then((a) => {
      if (alive) setFiltered({ ...a, base: key, text: active });
    });
    return () => { alive = false; };
  }, [near, filterQuery, filterKey, key, active, count, filterAttempt]);

  const currentFiltered = filtered?.key === filterKey && filtered.attempt === filterAttempt ? filtered : null;
  // while newer text runs, the results it replaces stay in view (the last filter's, else the saved ones)
  const held = (filtered?.base === key && filtered.status === 'ready' ? filtered : null) ?? current;
  const shown = active ? currentFiltered ?? held : current;
  const narrowing = !!active && !currentFiltered;
  const status = shown?.status ?? 'loading';
  const shownText = shown && 'text' in shown ? (shown as Filtered).text : '';

  const onType = (value: string) => {
    setTyped(value);
    clearTimeout(timer.current);
    if (!value.trim()) setFilter('');
    else timer.current = setTimeout(() => setFilter(value.trim()), FILTER_DEBOUNCE_MS);
  };
  const clear = () => {
    onType('');
    inputRef.current?.focus();
  };

  const editLink = (
    <button type="button" className="edit-section-link" onClick={() => onEdit(search)}>Edit search</button>
  );
  const seeAll = showBox && typed.trim() ? withFilterText(asked, typed) : asked;
  const menu: MenuAction[] = [
    { label: 'Edit search', onSelect: () => onEdit(search) },
    { label: 'Move up', onSelect: () => onMove(-1), disabled: !canMoveUp },
    { label: 'Move down', onSelect: () => onMove(1), disabled: !canMoveDown },
    ...filterBoxModes.map((mode, i): MenuAction => ({
      label: `Filter box: ${FILTER_BOX_LABELS[mode]}`,
      checked: mode === filterBox,
      divided: i === 0,
      onSelect: () => onFilterBox(mode),
    })),
    { label: 'Remove from Discover', onSelect: onRemove, divided: filterBoxModes.length > 0 },
  ];

  const renderBody = () => {
    if (status === 'loading') return <SectionSkeleton variant={variant}/>;
    if (status === 'error') {
      return (
        <SectionMessage onRetry={() => (active ? setFilterAttempt((a) => a + 1) : setAttempt((a) => a + 1))}>
          Couldn't run this saved search.
        </SectionMessage>
      );
    }
    if (status === 'invalid') {
      return (
        <div className="edit-section-msg is-compact" role="alert">
          <span>This search can't run as it is</span>
          <span aria-hidden="true">·</span>
          {editLink}
        </div>
      );
    }
    if (shown!.items.length === 0 && shownText) {
      return (
        <div className="edit-section-msg is-compact">
          <span>Nothing in {search.title} matches “{shownText}”</span>
          <span aria-hidden="true">·</span>
          <button type="button" className="edit-section-link" onClick={clear}>Clear</button>
        </div>
      );
    }
    if (shown!.items.length === 0) {
      return (
        <div className="edit-section-msg is-compact">
          <span>Nothing matches right now</span>
          <span aria-hidden="true">·</span>
          {editLink}
        </div>
      );
    }
    return <ItemsView variant={variant} items={shown!.items} navigate={navigate}/>;
  };

  return (
    <section
      ref={ref}
      className={`${className}${variant === 'carousel' ? ' is-shelf' : ''}${narrowing ? ' is-filtering' : ''}`}
      aria-label={search.title}
      aria-busy={status === 'loading' || narrowing}
      data-section={section.id}
    >
      <SectionHead
        icon={faBookmark}
        kicker="Saved search"
        title={search.title}
        description={search.description}
        filter={showBox && (
          <div className="edit-filter-field">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="edit-filter-icon" aria-hidden="true"/>
            <input
              ref={inputRef}
              type="search"
              className="edit-filter-input"
              aria-label={`Filter ${search.title}`}
              placeholder="Filter"
              maxLength={filterTextRoom(asked)}
              value={typed}
              onChange={(e) => onType(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && typed) { e.preventDefault(); e.stopPropagation(); clear(); }
              }}
            />
            {typed && (
              <button type="button" className="edit-filter-clear" aria-label="Clear filter" onClick={clear}>
                <FontAwesomeIcon icon={faXmark} className="text-[10px]"/>
              </button>
            )}
          </div>
        )}
        actions={(
          <>
            <Link className="edit-section-link" to={{ pathname: DISCOVER_PATH, search: queryToSearch(seeAll) }}>
              See all
            </Link>
            <SectionMenu label={`Options for ${search.title}`} actions={menu}/>
          </>
        )}
      />
      {renderBody()}
    </section>
  );
}
